<template>
    <v-card-text>
        <v-card>
            <v-card-title>
                <v-row no-gutters>
                    Experiment Design Configuration
                    <v-spacer />
                    <v-btn v-if="isEditing" variant="elevated" @click="cancel">
                        Cancel
                    </v-btn>
                    <v-btn v-if="isEditing" variant="elevated" color="primary" @click="save" class="ms-4">
                        Save Configuration
                    </v-btn>
                    <v-btn v-else variant="elevated" color="primary" @click="isEditing = true" append-icon="$pencil">
                        Edit Configuration
                    </v-btn>
                </v-row>
            </v-card-title>
            <v-card-text>
                <design-config-form
                    ref="experimentDesignConfigForm"
                    :model-value="experimentDesignConfig"
                    :experimental-unit-options="experimentalUnitOptions"
                    :disabled="!isEditing"
                    @update:modelValue="onDesignConfigFormChanged"
                />
            </v-card-text>
        </v-card>
    </v-card-text>
</template>
<script setup lang="ts">
import DesignConfigForm from "@/components/ExperimentDesignConfigs/DesignConfigForm.vue";
import {
    ExperimentalUnitOption,
    ExperimentDesignConfig,
    experimentDesignConfigsAPI,
} from "@/services/experimentDesignConfigs/experimentDesignConfigsAPI";
import { ref, onMounted } from "vue";
import { withLoadingScreen } from "@/lib/loading";
import { useToast } from "vue-toastification";

const toast = useToast();

const { experimentGroupId } = defineProps<{
    experimentGroupId: string;
}>();

onMounted(() => {
    fetchData();
});

const isEditing = ref<boolean>(false);

const experimentDesignConfigForm = ref();
const experimentDesignConfig = ref<ExperimentDesignConfig | null>(null);
const experimentalUnitOptions = ref<ExperimentalUnitOption[]>([]);

function onDesignConfigFormChanged(updatedExperimentDesignConfig: ExperimentDesignConfig) {
    experimentDesignConfig.value = updatedExperimentDesignConfig;
}

function cancel() {
    isEditing.value = false;
    fetchData();
}

async function save() {
    const designConfig = experimentDesignConfig.value;
    if (designConfig && (await experimentDesignConfigForm.value.validate())) {
        await withLoadingScreen(async () => {
            if ("id" in designConfig) {
                const id = designConfig.id as string;
                await experimentDesignConfigsAPI.updateExperimentDesignConfigForExperimentGroup(
                    experimentGroupId,
                    id,
                    designConfig
                );
            } else {
                await experimentDesignConfigsAPI.createExperimentDesignConfigForExperimentGroup(
                    experimentGroupId,
                    designConfig
                );
            }
        });
        isEditing.value = false
        toast.success("Experiment design config saved successfully");
    }
}

async function fetchData() {
    await withLoadingScreen(async () => {
        const experimentalDesignConfigWithOptions = await experimentDesignConfigsAPI.fetchForExperimentGroupId(experimentGroupId);
        experimentDesignConfig.value = experimentalDesignConfigWithOptions.experimentDesignConfig;
        experimentalUnitOptions.value = experimentalDesignConfigWithOptions.experimentalUnitOptionsFromSchema;
    });
}

</script>
